import APIService from "./APIService";
//testing for real!
//first integration commit

const UserAPI = {
  getUserByUuid: async (userUuid) => {
    // Get specific user by UUID parameter - for cases where we need a specific user
    return APIService.get(`/api/v1/base/users/uuid/${userUuid}`);
  },

  getCurrentUser: async () => {
    // Get current logged-in user using cookie-based authentication
    return APIService.get(`/api/v1/base/users/by-uuid`);
  },

  /**
   * Create a new user
   * @param {Object} data - User data to create
   * @param {Boolean} [sendVerification=false] - Whether to send a verification email
   * @returns {Promise} - Promise that resolves with the created user
   */
  createUser: async (data, sendVerification = false) => {
    return APIService.post(
      `/api/v1/base/users/member?sendVerification=${sendVerification}`,
      data,
    );
  },

  /**
   * Create a new account owner user (user with login credentials)
   * @param {Object} data - Account owner data to create
   * @returns {Promise} - Promise that resolves with the created account owner
   */
  createAccountOwnerUser: async (data) => {
    return APIService.post(`/api/v1/base/users/account`, data);
  },
  updateUser: async (data) => {
    return APIService.put(`/api/v1/base/users`, data);
  },

  getAllUsers: async () => {
    return APIService.get(`/api/v1/base/users`);
  },

  /**
   * Get users in the authenticated user's group
   * SECURITY: Uses context-based endpoint - no groupId exposure
   * @returns {Promise} - Promise that resolves with users in the authenticated user's group
   */
  getUsersInMyGroup: async () => {
    return APIService.get(`/api/v1/base/users/my-group`);
  },

  /**
   * Get user count in the authenticated user's group
   * SECURITY: Uses context-based endpoint - no groupId exposure
   * @returns {Promise} - Promise that resolves with count object { count: number }
   */
  getUserCountInMyGroup: async () => {
    return APIService.get(`/api/v1/base/users/my-group/count`);
  },

  /**
   * @deprecated Use getUsersInMyGroup() instead. This method exposes groupId.
   */
  getUsersByGroupId: async (groupId) => {
    console.warn(
      "DEPRECATED: getUsersByGroupId exposes groupId - use getUsersInMyGroup() instead",
    );
    return APIService.get(`/api/v1/base/users/group/${groupId}`);
  },

  /**
   * @deprecated Use getUserCountInMyGroup() instead. This method exposes groupId.
   */
  getUserCountByGroupId: async (groupId) => {
    console.warn(
      "DEPRECATED: getUserCountByGroupId exposes groupId - use getUserCountInMyGroup() instead",
    );
    return APIService.get(`/api/v1/base/users/group/${groupId}/count`);
  },

  // Activate specific user by UUID (for other users)
  activateUserByUuid: async (userUuid) => {
    return APIService.patch(`/api/v1/base/users/${userUuid}/activate`, {});
  },

  // Deactivate specific user by UUID (for other users)
  deactivateUserByUuid: async (userUuid) => {
    return APIService.patch(`/api/v1/base/users/${userUuid}/deactivate`, {});
  },

  // Activate current logged-in user (self)
  activateCurrentUser: async () => {
    return APIService.patch(`/api/v1/base/users/activate`, {});
  },

  // Deactivate current logged-in user (self)
  deactivateCurrentUser: async () => {
    return APIService.patch(`/api/v1/base/users/deactivate`, {});
  },

  // Delete multiple users
  deleteMultipleUsers: async (userUuids) => {
    console.log("UserAPI.deleteMultipleUsers called with:", userUuids);
    console.log("userUuids is Array:", Array.isArray(userUuids));
    console.log("userUuids JSON:", JSON.stringify(userUuids));
    return APIService.post(`/api/v1/base/users/delete-multiple`, { userUuids });
  },

  // Check if a user is an account owner by user UUID
  checkAccountOwnershipByUuid: async (uuid) => {
    try {
      const response = await APIService.get(
        `/api/v1/base/users/account-ownership?uuid=${uuid}`,
      );
      return response.data || response;
    } catch (error) {
      console.error(`Error checking account ownership:`, error);
      return { isAccountOwner: false };
    }
  },

  // Get account ownership status for current logged-in user
  getUserAccountOwnership: async () => {
    try {
      const response = await APIService.get(
        `/api/v1/base/users/account-ownership`,
      );
      return response.data || response;
    } catch (error) {
      // Silently handle 403 errors - this is expected for non-account-owner users
      // Return false instead of throwing to prevent error popups
      console.log(
        "⚠️ User is not an account owner (expected for member users)",
      );
      return { isAccountOwner: false };
    }
  },

  // Get user verification status using cookie-based authentication
  getUserVerificationStatus: async () => {
    try {
      const response = await APIService.get(`/api/v1/base/users/verified`);
      return response.data !== undefined ? response.data : response;
    } catch (error) {
      console.error("Error getting verification status:", error);
      throw error;
    }
  },

  changePassword: async (currentPassword, newPassword) => {
    try {
      const response = await APIService.put(
        `/api/v1/base/credentials/user/change-password`,
        {
          currentPassword,
          newPassword,
        },
      );
      return response.data !== undefined ? response.data : response;
    } catch (error) {
      console.error(`Error changing password:`, error);
      throw error;
    }
  },

  /**
   * Update user's group assignment by group name
   * SECURITY: Uses groupName instead of groupId
   * @param {string} userUuid - User's UUID
   * @param {string} groupName - Name of the group to assign the user to
   * @returns {Promise} - Promise that resolves with the updated user
   */
  updateUserGroupByName: async (userUuid, groupName) => {
    try {
      const response = await APIService.put(
        `/api/v1/base/users/${userUuid}/group-by-name`,
        {
          groupName,
        },
      );
      return response.data !== undefined ? response.data : response;
    } catch (error) {
      console.error(`Error updating group for user UUID ${userUuid}:`, error);
      throw error;
    }
  },

  /**
   * @deprecated Use updateUserGroupByName() instead. This method exposes groupId.
   */
  updateUserGroup: async (userUuid, groupId) => {
    console.warn(
      "DEPRECATED: updateUserGroup exposes groupId - use updateUserGroupByName() instead",
    );
    try {
      const response = await APIService.put(
        `/api/v1/base/users/${userUuid}/group`,
        {
          groupId,
        },
      );
      return response.data !== undefined ? response.data : response;
    } catch (error) {
      console.error(`Error updating group for user UUID ${userUuid}:`, error);
      throw error;
    }
  },

  // Set password for new users who don't have a current password
  // Uses secure token-based authentication (token from email verification)
  setNewUserPassword: async (setupToken, newPassword) => {
    try {
      const response = await APIService.post(
        `/api/v1/base/credentials/new-user-password`,
        {
          setupToken,
          newPassword,
        },
      );
      return response.data !== undefined ? response.data : response;
    } catch (error) {
      console.error(`Error setting password for new user:`, error);
      throw error;
    }
  },

  /**
   * Get users based on current user's role and permissions
   * @returns {Promise} - Promise that resolves with users in current user's scope
   */
  getUsersInMyScope: async () => {
    return APIService.get(`/api/v1/base/users/in-my-scope`);
  },

  /**
   * Verify user's phone number with SMS code
   * @param {string} userUuid - User's UUID
   * @param {Object} data - Verification data containing verificationCode
   * @returns {Promise} - Promise that resolves with verification result
   */
  verifyUserPhone: async (userUuid, data) => {
    return APIService.post(`/api/v1/base/users/${userUuid}/verify-phone`, data);
  },

  /**
   * Send phone verification code
   * @param {string} userUuid - User's UUID
   * @returns {Promise} - Promise that resolves with send result
   */
  sendPhoneVerification: async (userUuid) => {
    return APIService.post(
      `/api/v1/base/users/${userUuid}/send-phone-verification`,
      {},
    );
  },

  /**
   * Get detailed verification status for a user
   * @param {string} userUuid - User's UUID
   * @returns {Promise} - Promise that resolves with verification status
   */
  getVerificationStatus: async (userUuid) => {
    return APIService.get(`/api/v1/base/users/${userUuid}/verification-status`);
  },

  /**
   * Resend verification email to an unverified member user.
   * Resets the member's verification state and sends a fresh 24-hour link.
   * Only callable by account_admin or platform_admin.
   * @param {string} userUuid - The UUID of the unverified member user
   * @returns {Promise} - Promise that resolves with the resend result
   */
  resendMemberVerification: async (userUuid) => {
    return APIService.post(
      `/api/v1/base/users/${userUuid}/resend-member-verification`,
      {},
    );
  },
};

export default UserAPI;
